import { all, call, put, retry, takeEvery } from 'redux-saga/effects';
import { accountingActions, siteActions } from '../actions';
import {
    AddJournalEntryAction,
    FeeTypeList,
    GetJournalEntriesActionBegin,
    InvoiceType,
    JournalEntryList,
    DownloadInvoiceAction,
    AddLineItemsAction,
    InvoiceUpdateActionType,
} from '../modules/accounting/types';
import { Api, API_CONFIGURATIONS } from '../services/services';
import { getAccessToken } from './authentication.sagas';
import ACTIONS from '../app.constants';

function* getInvoiceTypes() {
    try {
        yield put(accountingActions.getInvoiceTypes.pending);
        const token: string = yield call(getAccessToken, 'invoiceTypes.read');
        const invoiceTypes: InvoiceType[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getInvoiceTypes,
            token,
        );
        invoiceTypes.sort((a, b) => a.name.localeCompare(b.name));
        yield put(accountingActions.getInvoiceTypes.success(invoiceTypes));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(accountingActions.getInvoiceTypes.error(error));
    }
}

function* getFeeTypes() {
    try {
        yield put(accountingActions.getFeeTypes.pending);
        const token: string = yield call(getAccessToken, 'feeTypes.read');
        const feeTypes: FeeTypeList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getFeeTypes,
            token,
        );
        feeTypes.sort((a, b) => a.name.localeCompare(b.name));
        yield put(accountingActions.getFeeTypes.success(feeTypes));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(accountingActions.getFeeTypes.error(error));
    }
}

function* getJournalEntries(action: GetJournalEntriesActionBegin) {
    try {
        yield put(accountingActions.getJournalEntries.pending);
        const token: string = yield call(getAccessToken, 'journalEntries.read');
        const journalEntries: JournalEntryList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getJournalEntries,
            token,
            action.payload.params,
        );
        yield put(accountingActions.getJournalEntries.success(journalEntries));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(accountingActions.getJournalEntries.error(error));
    }
}

function* addJournalEntry(action: AddJournalEntryAction) {
    try {
        yield put(accountingActions.addJournalEntry.pending);
        const token: string = yield call(getAccessToken, 'journalEntries.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addJournalEntry,
            token,
            action.payload,
        );

        yield put(accountingActions.addJournalEntry.success);
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(accountingActions.addJournalEntry.error(error));
    }
}

function* addLineItems(action: AddLineItemsAction) {
    try {
        yield put(accountingActions.addLineItems.pending);
        const token: string = yield call(getAccessToken, 'journalEntries.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addLineItems,
            token,
            action.payload.invoiceId,
            action.payload.lineItems,
        );
        yield put(accountingActions.addLineItems.success);
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(accountingActions.addLineItems.error(error));
    }
}

function* voidInvoice(action: InvoiceUpdateActionType) {
    const invoiceId: string = action.payload.invoiceId;
    const licenseId: string = action.payload.licenseId;
    const personId: string = action.payload.personId;

    try {
        yield put(accountingActions.voidInvoice.pending);
        const token: string = yield call(getAccessToken, 'journalEntries.write');

        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.voidInvoice,
            token,
            invoiceId,
        );

        yield put(
            accountingActions.getJournalEntriesBegin({
                filter: {
                    where: {
                        type: 'Invoice',
                        [licenseId ? 'lineItems.license.id' : 'lineItems.person.id']: licenseId ?? personId,
                    },
                },
            }),
        );

        yield put(accountingActions.voidInvoice.success);
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Information has been saved.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(accountingActions.voidInvoice.error(error));
    }
}

function* downloadInvoice(action: DownloadInvoiceAction) {
    try {
        yield put(accountingActions.downloadInvoices.pending);
        const token: string = yield call(getAccessToken, 'journalEntries.write');
        const mergeTemplateToken: string = yield call(getAccessToken, 'mergeTemplates.read');
        const result: any = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getInvoiceType,
            token,
            action.payload.invoiceTypeId,
        );
        if (result.mergeTemplates && result.mergeTemplates.invoice) {
            const downloadResponse: { response: string; status: number } = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.downloadInvoice,
                mergeTemplateToken,
                result?.mergeTemplates.invoice,
                action.payload.fileName,
                action.payload.invoiceId,
            );
            if (downloadResponse.status === 200) {
                yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: downloadResponse.response }));
            } else {
                yield put(siteActions.setError({ isError: true, errorCode: 1, message: downloadResponse.response }));
            }
            yield put(accountingActions.downloadInvoices.success);
        } else {
            yield put(
                siteActions.setError({
                    isError: true,
                    errorCode: 1,
                    message: 'Something went wrong. Please contact IT support',
                }),
            );
        }
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(accountingActions.downloadInvoices.error(error));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(ACTIONS.GET_INVOICE_TYPES_BEGIN, getInvoiceTypes),
        takeEvery(ACTIONS.GET_FEE_TYPES_BEGIN, getFeeTypes),
        takeEvery(ACTIONS.GET_JOURNAL_ENTRIES_BEGIN, getJournalEntries),
        takeEvery(ACTIONS.ADD_JOURNAL_ENTRY_BEGIN, addJournalEntry),
        takeEvery(ACTIONS.ADD_LINE_ITEMS_BEGIN, addLineItems),
        takeEvery(ACTIONS.VOID_INVOICE_BEGIN, voidInvoice),
        takeEvery(ACTIONS.DOWNLOAD_INVOICE_BEGIN, downloadInvoice),
    ]);
}
